import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Truck, Container, Handshake } from "lucide-react";
import SEO from "../components/SEO";
import CTASection from "../components/CTASection";
import { Reveal, MaskedLine, Eyebrow } from "../components/Reveal";
import { IMAGES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = {
    name: "",
    email: "",
    phone: "",
    cdl_class: "",
    experience: "",
    endorsements: "",
    message: "",
};

const inputClass =
    "w-full border border-gray-300 bg-white px-4 py-3 text-[15px] text-ink placeholder:text-gray-400 transition-colors focus:border-gold focus:outline-none";

const EXPECTATIONS = [
    {
        icon: Container,
        title: "Steady Container Work",
        copy: "High-volume drayage across the LA/LB port complex — 900+ containers move through our operation every week.",
    },
    {
        icon: Truck,
        title: "Real Equipment Behind You",
        copy: "100+ trucks, 500+ chassis and a dual-yard operation mean you spend your day driving, not waiting on equipment.",
    },
    {
        icon: Handshake,
        title: "Family-Owned Since 2002",
        copy: "You know who you work for. Direct communication with dispatch and a company built on long-term relationships.",
    },
];

function Field({ label, id, testid, required, error, children }) {
    return (
        <div>
            <label
                htmlFor={id}
                className="mb-2 block font-heading text-xs font-bold uppercase tracking-[0.18em] text-ink"
            >
                {label} {required && <span className="text-gold" aria-hidden="true">*</span>}
            </label>
            {children}
            {error && (
                <p id={`${id}-error`} role="alert" className="mt-1.5 text-sm text-red-600" data-testid={`${testid}-error`}>
                    {error}
                </p>
            )}
        </div>
    );
}

export default function Drive() {
    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const update = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setErrors((err) => ({ ...err, [key]: undefined }));
    };

    const validate = () => {
        const next = {};
        if (!form.name.trim()) next.name = "Please enter your name.";
        if (!form.email.trim()) next.email = "Please enter your email.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
            next.email = "Please enter a valid email address.";
        if (!form.phone.trim()) next.phone = "Please enter your phone number.";
        if (!form.cdl_class) next.cdl_class = "Please select your CDL class.";
        return next;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        const next = validate();
        setErrors(next);
        if (Object.keys(next).length > 0) return;
        setSubmitting(true);
        try {
            await axios.post(`${API}/driver-applications`, {
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                cdl_class: form.cdl_class,
                experience: form.experience || null,
                endorsements: form.endorsements.trim() || null,
                message: form.message.trim() || null,
            });
            toast.success("Application sent.", {
                description: "Our team will review it and reach out shortly.",
            });
            setForm(INITIAL_FORM);
        } catch (err) {
            toast.error("Something went wrong.", {
                description: "Please try again, or call us at (310) 363-0713.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <SEO
                title="Drive With Pacific | Driver Careers at Pacific Expressway"
                description="Drive for a family-owned Southern California logistics company. High-volume LA/LB port drayage backed by 100+ trucks, 500+ chassis and a dual-yard operation. Apply today."
                path="/drive"
            />
            <section
                className="relative flex min-h-[52svh] items-end overflow-hidden bg-ink"
                data-testid="drive-hero"
            >
                <div className="absolute inset-0" aria-hidden="true">
                    <img src={IMAGES.semiHighway} alt="" className="h-full w-full object-cover opacity-45" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" aria-hidden="true" />
                <div className="container-px relative w-full pb-14 pt-40 lg:pb-20">
                    <Eyebrow>Careers at Pacific</Eyebrow>
                    <h1 className="mt-6 max-w-3xl font-heading text-3xl sm:text-4xl lg:text-[56px] font-bold tracking-tight leading-[1.15] sm:leading-[1.1] lg:leading-[1.06] text-white">
                        <MaskedLine delay={0.2}>
                            Drive With Pacific<span className="text-gold">.</span>
                        </MaskedLine>
                    </h1>
                    <Reveal delay={0.4} className="mt-6 max-w-2xl">
                        <p className="text-base sm:text-lg leading-relaxed text-gray-300">
                            We&rsquo;re always interested in meeting professional drivers who
                            want consistent work and a company that invests in its own
                            equipment — not excuses.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-white py-16 sm:py-20 lg:py-24" data-testid="drive-expectations">
                <div className="container-px">
                    <div className="grid gap-px bg-gray-200 sm:grid-cols-3">
                        {EXPECTATIONS.map((item, i) => (
                            <Reveal key={item.title} delay={i * 0.08} className="bg-white">
                                <div className="h-full p-7 sm:p-9">
                                    <item.icon size={26} className="text-gold" aria-hidden="true" strokeWidth={1.75} />
                                    <h2 className="mt-5 font-heading text-lg sm:text-xl font-semibold text-ink">
                                        {item.title}
                                    </h2>
                                    <p className="mt-3 text-[15px] leading-relaxed text-gray-600">
                                        {item.copy}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-mist py-16 sm:py-20 lg:py-24" data-testid="drive-form-section">
                <div className="container-px grid gap-10 lg:grid-cols-5 lg:gap-12">
                    <Reveal className="lg:col-span-3">
                        <form
                            onSubmit={handleSubmit}
                            noValidate
                            data-testid="driver-form"
                            className="bg-white p-6 sm:p-10 border border-gray-200"
                        >
                            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-ink">
                                Driver Application
                            </h2>
                            <p className="mt-3 text-[15px] text-gray-600">
                                Tell us about yourself — a real person reviews every
                                application.
                            </p>
                            <div className="mt-8 grid gap-6 sm:grid-cols-2">
                                <Field label="Name" id="driver-name" testid="driver-form-name" required error={errors.name}>
                                    <input
                                        id="driver-name"
                                        data-testid="driver-form-name"
                                        type="text"
                                        autoComplete="name"
                                        value={form.name}
                                        onChange={update("name")}
                                        aria-invalid={!!errors.name}
                                        aria-describedby={errors.name ? "driver-name-error" : undefined}
                                        className={inputClass}
                                    />
                                </Field>
                                <Field label="Phone" id="driver-phone" testid="driver-form-phone" required error={errors.phone}>
                                    <input
                                        id="driver-phone"
                                        data-testid="driver-form-phone"
                                        type="tel"
                                        autoComplete="tel"
                                        value={form.phone}
                                        onChange={update("phone")}
                                        aria-invalid={!!errors.phone}
                                        aria-describedby={errors.phone ? "driver-phone-error" : undefined}
                                        className={inputClass}
                                    />
                                </Field>
                                <Field label="Email" id="driver-email" testid="driver-form-email" required error={errors.email}>
                                    <input
                                        id="driver-email"
                                        data-testid="driver-form-email"
                                        type="email"
                                        autoComplete="email"
                                        value={form.email}
                                        onChange={update("email")}
                                        aria-invalid={!!errors.email}
                                        aria-describedby={errors.email ? "driver-email-error" : undefined}
                                        className={inputClass}
                                    />
                                </Field>
                                <Field label="CDL Class" id="driver-cdl" testid="driver-form-cdl" required error={errors.cdl_class}>
                                    <select
                                        id="driver-cdl"
                                        data-testid="driver-form-cdl"
                                        value={form.cdl_class}
                                        onChange={update("cdl_class")}
                                        aria-invalid={!!errors.cdl_class}
                                        aria-describedby={errors.cdl_class ? "driver-cdl-error" : undefined}
                                        className={inputClass}
                                    >
                                        <option value="">Select…</option>
                                        <option value="Class A">Class A</option>
                                        <option value="Class B">Class B</option>
                                        <option value="No CDL yet">No CDL yet</option>
                                    </select>
                                </Field>
                                <Field label="Years of Experience" id="driver-experience" testid="driver-form-experience">
                                    <select
                                        id="driver-experience"
                                        data-testid="driver-form-experience"
                                        value={form.experience}
                                        onChange={update("experience")}
                                        className={inputClass}
                                    >
                                        <option value="">Select…</option>
                                        <option value="Less than 1 year">Less than 1 year</option>
                                        <option value="1–3 years">1–3 years</option>
                                        <option value="3–5 years">3–5 years</option>
                                        <option value="5+ years">5+ years</option>
                                    </select>
                                </Field>
                                <Field label="Endorsements" id="driver-endorsements" testid="driver-form-endorsements">
                                    <input
                                        id="driver-endorsements"
                                        data-testid="driver-form-endorsements"
                                        type="text"
                                        placeholder="e.g. Hazmat, Tanker, TWIC"
                                        value={form.endorsements}
                                        onChange={update("endorsements")}
                                        className={inputClass}
                                    />
                                </Field>
                                <div className="sm:col-span-2">
                                    <Field label="Message" id="driver-message" testid="driver-form-message">
                                        <textarea
                                            id="driver-message"
                                            data-testid="driver-form-message"
                                            rows={4}
                                            placeholder="Anything you'd like us to know — port experience, availability, routes you've run…"
                                            value={form.message}
                                            onChange={update("message")}
                                            className={`${inputClass} resize-y`}
                                        />
                                    </Field>
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="driver-form-submit-button"
                                className="group mt-8 inline-flex w-full items-center justify-center gap-2 bg-gold px-8 py-4 font-heading text-sm font-bold uppercase tracking-[0.14em] text-ink transition-colors duration-200 hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                            >
                                {submitting ? "Sending…" : "Submit Application"}
                                {!submitting && (
                                    <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                                )}
                            </button>
                        </form>
                    </Reveal>
                    <Reveal delay={0.12} className="lg:col-span-2">
                        <div className="group relative h-full min-h-[320px] overflow-hidden">
                            <img
                                src={IMAGES.fleetTruck}
                                alt="Pacific Expressway truck on a Southern California route"
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/30 to-transparent" />
                            <div className="absolute inset-x-0 bottom-0 p-7 sm:p-9">
                                <p className="font-heading text-xl sm:text-2xl font-bold text-white">
                                    Prefer to talk?
                                </p>
                                <p className="mt-2 text-[15px] text-gray-300">
                                    Call dispatch and ask about driving opportunities.
                                </p>
                                <a
                                    href="tel:+13103630713"
                                    data-testid="drive-call-link"
                                    className="mt-4 inline-flex items-center gap-2 font-heading text-lg font-bold text-gold"
                                >
                                    (310) 363-0713
                                </a>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CTASection
                eyebrow="Shippers"
                title="Need Freight Moved Instead?"
                copy="Tell us what you're moving and where it needs to go. Our team will help determine the right logistics solution."
            />
        </>
    );
}
